import { MassTortMattersListView } from "@/components/features/mass-torts/MassTortMattersListView";
import { UpdateMassTortModal } from "@/components/features/mass-torts/UpdateMassTortModal";
import { MatterOverViewNotesTasksEventsSidebar } from "@/components/features/matter/overview/MatterOverViewNotesTasksEventsSidebar";
import LawWorksLoader from "@/components/ui/LawWorksLoader/LawWorksLoader";
import { useUser } from "@/components/UserProvider";
import {
  AppPageShell,
  Button,
  Sheet,
  SheetContent,
  Typography,
} from "@/components/ui";
import { useSidebar } from "@/components/ui/shadcn/Sidebar/Sidebar";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { retrieveMassTortQueryOptions } from "@/lib/data/react-query/mass-torts/mass-tort";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { getMassTortAnnotationMatter } from "@/lib/utils/massTortAnnotationUtils";
import { useQuery } from "@tanstack/react-query";
import { NotebookTabs, Pencil, SidebarIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type MassTortOverviewViewProps = {
  massTortSearchId: string;
};

export function MassTortOverviewView({
  massTortSearchId: massTortId,
}: MassTortOverviewViewProps) {
  const {
    data: massTortResponse,
    isLoading: isMassTortLoading,
    isError: isMassTortError,
  } = useQuery({
    ...retrieveMassTortQueryOptions(massTortId),
  });

  const massTort = massTortResponse?.data;
  const annotationMatter = useMemo(
    () => getMassTortAnnotationMatter(massTort),
    [massTort],
  );
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isEditingMassTort, setIsEditingMassTort] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const { toggleSidebar } = useSidebar();
  const { hasActionPermission } = useUser();
  const canUpdateMassTort = hasActionPermission(
    ACTION_PERMISSION_KEYS.massTort,
    ACTION_PERMISSION_VALUES.update,
  );

  useEffect(() => {
    if (isDesktop) {
      setIsSidebarOpen(false);
    }
  }, [isDesktop]);

  const isPageLoading = Boolean(massTortId && isMassTortLoading);

  return (
    <AppPageShell
      padding="none"
      showMobileSidebarToggle={false}
      className="h-full min-h-0 overflow-hidden"
    >
      {isPageLoading ? (
        <LawWorksLoader className="w-full" />
      ) : (
        <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:overflow-hidden">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:overflow-hidden">
            {!isDesktop && (
              <div className="bg-background/90 border-border sticky top-0 z-10 mb-4 flex items-center justify-between gap-2 rounded-xl border p-4 shadow-xs backdrop-blur lg:hidden">
                <Button
                  size="icon"
                  variant="outline"
                  className="shrink-0"
                  onClick={toggleSidebar}
                  aria-label="Open app sidebar"
                >
                  <SidebarIcon className="h-4 w-4" />
                </Button>
                <div className="flex flex-1 justify-end">
                  <Sheet open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
                    <Button
                      size="sm"
                      variant="default"
                      className="w-full gap-2 font-semibold shadow sm:w-auto"
                      onClick={() => {
                        setIsSidebarOpen(true);
                      }}
                      aria-expanded={isSidebarOpen}
                      aria-controls="mass-tort-overview-sidebar-sheet"
                      disabled={!massTortId || !massTort}
                    >
                      <NotebookTabs className="h-4 w-4" />
                      Notes, tasks & events
                    </Button>
                    <SheetContent
                      side="right"
                      className="w-full max-w-[370px] p-0 sm:!w-[370px] sm:!max-w-[370px] sm:!min-w-[370px]"
                      id="mass-tort-overview-sidebar-sheet"
                    >
                      <div className="h-full overflow-y-auto">
                        <MatterOverViewNotesTasksEventsSidebar
                          matter={annotationMatter}
                          massTortId={massTortId}
                        />
                      </div>
                    </SheetContent>
                  </Sheet>
                </div>
              </div>
            )}
            <div className="border-border bg-card flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border shadow-xs">
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-4 sm:p-6">
                <header className="border-border shrink-0 space-y-2 border-b pb-6">
                  {isMassTortError ? (
                    <>
                      <Typography variant="heading" className="text-3xl">
                        Mass tort
                      </Typography>
                      <Typography className="text-muted-foreground">
                        Unable to load mass tort details.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 space-y-2">
                          <Typography variant="heading" className="text-3xl">
                            {massTort?.title?.trim() || "Mass tort"}
                          </Typography>
                          {massTort?.description?.trim() ? (
                            <Typography className="text-muted-foreground max-w-3xl leading-relaxed">
                              {massTort.description}
                            </Typography>
                          ) : null}
                        </div>
                        {canUpdateMassTort && massTortId && massTort ? (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="shrink-0"
                            onClick={() => {
                              setIsEditingMassTort(true);
                            }}
                            aria-label="Edit mass tort"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                    </>
                  )}
                </header>

                <div className="flex min-h-0 flex-1 flex-col pt-6">
                  <div className="mb-4 flex shrink-0 flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <Typography variant="subHeading" className="text-xl">
                        Matters
                      </Typography>
                    </div>
                  </div>
                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    <MassTortMattersListView massTortId={massTortId} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {massTortId && massTort ? (
            <div className="border-border bg-card hidden h-full min-h-0 w-[370px] shrink-0 overflow-hidden rounded-xl border shadow-xs lg:block">
              {isDesktop && (
                <MatterOverViewNotesTasksEventsSidebar
                  matter={annotationMatter}
                  massTortId={massTortId}
                />
              )}
            </div>
          ) : null}
        </div>
      )}
      {massTortId && massTort ? (
        <UpdateMassTortModal
          open={isEditingMassTort}
          setOpen={setIsEditingMassTort}
          massTort={massTort}
          massTortId={massTortId}
        />
      ) : null}
    </AppPageShell>
  );
}
